import {Select, TextInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {InstanceSsoProviderResponse} from 'src/api/ProactApiClient';
import {
  SsoAttributeMappingPresetEnum,
  SsoProviderTypeEnum,
} from 'src/api/ProactApiClient';
import {
  formatSamlAcsUrl,
  formatSamlEntityId,
} from 'src/app/security/authentication/configureAmplify';
import {Button} from 'src/design/atoms/Button';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {TagsInput} from 'src/design/atoms/TagsInput';
import {ViewField} from 'src/design/atoms/ViewField';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {CopyActionIcon} from 'src/design/molecules/CopyActionIcon';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {
  useAdoptSsoProviderMutation,
  useCreateSsoProviderMutation,
  useDeleteSsoProviderMutation,
  useGetSsoProviderQuery,
  useUpdateSsoProviderMutation,
} from 'src/redux/queries/instanceSso';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {z} from 'zod';

const PRESET_OPTIONS = [
  {
    value: String(SsoAttributeMappingPresetEnum.EntraId),
    label: 'Microsoft Entra ID',
  },
  {value: String(SsoAttributeMappingPresetEnum.Okta), label: 'Okta'},
  {
    value: String(SsoAttributeMappingPresetEnum.GenericSaml),
    label: 'Generic SAML',
  },
];

const resolvePresetLabel = (preset: SsoAttributeMappingPresetEnum): string =>
  PRESET_OPTIONS.find(option => option.value === String(preset))?.label ?? '—';

const ssoProviderSchema = z
  .object({
    attributeMappingPreset: z.nativeEnum(SsoAttributeMappingPresetEnum),
    metadataUrl: z
      .string()
      .trim()
      .min(1, 'Metadata URL is required')
      .url('Enter a valid metadata URL'),
    emailClaim: z.string().trim(),
    givenNameClaim: z.string().trim(),
    familyNameClaim: z.string().trim(),
    domains: z
      .array(z.string().trim().min(1))
      .min(1, 'Add at least one email domain'),
  })
  .superRefine((values, ctx) => {
    if (
      values.attributeMappingPreset !==
      SsoAttributeMappingPresetEnum.GenericSaml
    ) {
      return;
    }
    const claims: ('emailClaim' | 'givenNameClaim' | 'familyNameClaim')[] = [
      'emailClaim',
      'givenNameClaim',
      'familyNameClaim',
    ];
    for (const claim of claims) {
      if (values[claim].length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [claim],
          message: 'Required for a generic SAML provider',
        });
      }
    }
  });

type SsoProviderFormValues = z.infer<typeof ssoProviderSchema>;

const EMPTY_FORM_VALUES: SsoProviderFormValues = {
  attributeMappingPreset: SsoAttributeMappingPresetEnum.EntraId,
  metadataUrl: '',
  emailClaim: '',
  givenNameClaim: '',
  familyNameClaim: '',
  domains: [],
};

const toFormValues = (
  provider: InstanceSsoProviderResponse | null | undefined
): SsoProviderFormValues =>
  provider
    ? {
        attributeMappingPreset: provider.attributeMappingPreset,
        metadataUrl: provider.metadataUrl ?? '',
        emailClaim: provider.emailClaim ?? '',
        givenNameClaim: provider.givenNameClaim ?? '',
        familyNameClaim: provider.familyNameClaim ?? '',
        domains: provider.domains ?? [],
      }
    : EMPTY_FORM_VALUES;

export interface SsoProviderSectionProps {
  instanceId: number;
}

export const SsoProviderSection: React.FC<SsoProviderSectionProps> = ({
  instanceId,
}) => {
  const {setAlert} = useAlert();
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isAdoptOpen, setIsAdoptOpen] = useState(false);

  const {
    data: provider,
    isLoading,
    isError,
    error,
  } = useGetSsoProviderQuery(instanceId);

  const [createSsoProvider, {isLoading: isCreating}] =
    useCreateSsoProviderMutation();
  const [updateSsoProvider, {isLoading: isUpdating}] =
    useUpdateSsoProviderMutation();
  const [adoptSsoProvider, {isLoading: isAdopting}] =
    useAdoptSsoProviderMutation();
  const [deleteSsoProvider, {isLoading: isDeleting}] =
    useDeleteSsoProviderMutation();

  const {control, handleSubmit, reset, watch, formState} =
    useForm<SsoProviderFormValues>({
      defaultValues: EMPTY_FORM_VALUES,
      resolver: zodResolver(ssoProviderSchema),
    });

  useEffect(() => {
    reset(toFormValues(provider));
  }, [provider, reset]);

  const selectedPreset = watch('attributeMappingPreset');
  const showClaimFields =
    selectedPreset === SsoAttributeMappingPresetEnum.GenericSaml;
  const showClaimViewFields =
    provider?.attributeMappingPreset ===
    SsoAttributeMappingPresetEnum.GenericSaml;
  const isBusy = isCreating || isUpdating || isAdopting || isDeleting;
  const providerLabel = provider?.providerName
    ? `the ${provider.providerName}`
    : 'this';
  const adoptTarget = provider?.providerName ?? 'this provider';

  const entityId = provider?.spEntityId ?? formatSamlEntityId();
  const acsUrl = provider?.acsUrl ?? formatSamlAcsUrl();

  const save = async (values: SsoProviderFormValues) => {
    const isGeneric =
      values.attributeMappingPreset ===
      SsoAttributeMappingPresetEnum.GenericSaml;
    const request = {
      instanceId,
      providerType: SsoProviderTypeEnum.Saml,
      metadataUrl: values.metadataUrl,
      attributeMappingPreset: values.attributeMappingPreset,
      emailClaim: isGeneric ? values.emailClaim : undefined,
      givenNameClaim: isGeneric ? values.givenNameClaim : undefined,
      familyNameClaim: isGeneric ? values.familyNameClaim : undefined,
      domains: values.domains,
    };

    try {
      if (provider) {
        await updateSsoProvider(request).unwrap();
        setAlert('Single sign-on settings saved.', 'success');
      } else {
        await createSsoProvider(request).unwrap();
        setAlert('Single sign-on enabled for this instance.', 'success');
      }
    } catch (submitFailure) {
      setAlert(parseErrorMessage(submitFailure), 'error');
    }
  };

  const handleAdopt = async () => {
    setIsAdoptOpen(false);
    try {
      await adoptSsoProvider(instanceId).unwrap();
      setAlert('Single sign-on is now managed in the app.', 'success');
    } catch (adoptFailure) {
      setAlert(parseErrorMessage(adoptFailure), 'error');
    }
  };

  const handleRemove = async () => {
    setIsRemoveOpen(false);
    try {
      await deleteSsoProvider(instanceId).unwrap();
      setAlert('Single sign-on removed for this instance.', 'success');
    } catch (removeFailure) {
      setAlert(parseErrorMessage(removeFailure), 'error');
    }
  };

  const statusPill = provider ? (
    <Pill
      text={provider.managedInApp ? 'Managed in app' : 'Managed in CDK'}
      color={provider.managedInApp ? 'green' : 'gray'}
    />
  ) : (
    <Pill text="Not configured" color="gray" />
  );

  const entityAcsRows = (
    <Grid gutter="md">
      <Grid.Col span={{base: 12, md: 6}}>
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <Box style={{flex: 1, minWidth: 0, wordBreak: 'break-all'}}>
            <ViewField
              label="Entity ID (Identifier / Audience URI)"
              value={entityId}
              data-testid="sso-sp-entity-id"
            />
          </Box>
          <CopyActionIcon value={entityId} />
        </Group>
      </Grid.Col>
      <Grid.Col span={{base: 12, md: 6}}>
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <Box style={{flex: 1, minWidth: 0, wordBreak: 'break-all'}}>
            <ViewField
              label="ACS URL (Reply URL)"
              value={acsUrl}
              data-testid="sso-acs-url"
            />
          </Box>
          <CopyActionIcon value={acsUrl} />
        </Group>
      </Grid.Col>
    </Grid>
  );

  const entityAcsHelpText = (
    <Text size="xs" c="dimmed" mt={4}>
      Give these two values to the customer&apos;s identity provider. They are
      shared by every instance on this user pool.
    </Text>
  );

  const serviceProviderBlock = (
    <Box>
      <Text fw={600} size="sm" c="dimmed">
        Values for your identity provider
      </Text>
      <Divider mt={4} mb="sm" />
      {entityAcsRows}
      {entityAcsHelpText}
    </Box>
  );

  const cancelButton = formState.isDirty ? (
    <Button
      variant="subtle"
      color="black"
      onClick={() => reset(toFormValues(provider))}
      disabled={isBusy}>
      Cancel
    </Button>
  ) : null;

  const presetField = (
    <Controller
      name="attributeMappingPreset"
      control={control}
      render={({field, fieldState}) => (
        <Select
          data-testid="form-field-attributeMappingPreset"
          label="Identity Provider Type"
          placeholder="Select the identity provider"
          data={PRESET_OPTIONS}
          value={field.value ? String(field.value) : null}
          onChange={value => field.onChange(Number(value))}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
          comboboxProps={{zIndex: 1001}}
        />
      )}
    />
  );

  const metadataUrlField = (
    <Controller
      name="metadataUrl"
      control={control}
      render={({field, fieldState}) => (
        <TextInput
          data-testid="form-field-metadataUrl"
          label="Metadata URL"
          placeholder="https://login.example.com/federationmetadata.xml"
          withAsterisk
          {...field}
          error={fieldState.error?.message}
        />
      )}
    />
  );

  const domainsField = (
    <Controller
      name="domains"
      control={control}
      render={({field, fieldState}) => (
        <TagsInput
          label="Email Domains"
          placeholder="acme.com"
          description="Type a domain such as acme.com and press Enter to add it. Users who sign in with these domains go to this provider."
          required
          name={field.name}
          value={field.value}
          onChangeCallback={field.onChange}
          error={fieldState.error?.message}
        />
      )}
    />
  );

  const claimFields = (
    <Grid gutter="md">
      <Grid.Col span={{base: 12, md: 4}}>
        <Controller
          name="emailClaim"
          control={control}
          render={({field, fieldState}) => (
            <TextInput
              data-testid="form-field-emailClaim"
              label="Email Claim URI"
              placeholder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
      </Grid.Col>
      <Grid.Col span={{base: 12, md: 4}}>
        <Controller
          name="givenNameClaim"
          control={control}
          render={({field, fieldState}) => (
            <TextInput
              data-testid="form-field-givenNameClaim"
              label="First Name Claim URI"
              placeholder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
      </Grid.Col>
      <Grid.Col span={{base: 12, md: 4}}>
        <Controller
          name="familyNameClaim"
          control={control}
          render={({field, fieldState}) => (
            <TextInput
              data-testid="form-field-familyNameClaim"
              label="Last Name Claim URI"
              placeholder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
      </Grid.Col>
    </Grid>
  );

  const claimViewRows = (
    <>
      <Grid.Col span={{base: 12, md: 4}}>
        <ViewField label="Email Claim URI" value={provider?.emailClaim} />
      </Grid.Col>
      <Grid.Col span={{base: 12, md: 4}}>
        <ViewField
          label="First Name Claim URI"
          value={provider?.givenNameClaim}
        />
      </Grid.Col>
      <Grid.Col span={{base: 12, md: 4}}>
        <ViewField
          label="Last Name Claim URI"
          value={provider?.familyNameClaim}
        />
      </Grid.Col>
    </>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      );
    }

    if (isError) {
      return <ErrorAlert message={parseErrorMessage(error)} mt={0} />;
    }

    if (!provider) {
      return (
        <form onSubmit={handleSubmit(save)}>
          <Stack gap="md">
            {entityAcsRows}
            {entityAcsHelpText}
            <Divider />
            <Text size="sm" c="dimmed" data-testid="sso-not-configured">
              Enabling SSO creates a SAML identity provider in Cognito from the
              metadata URL below, creates a dedicated Cognito app client for
              this instance, and routes sign-ins from the listed email domains
              to that provider.
            </Text>
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 4}}>{presetField}</Grid.Col>
              <Grid.Col span={{base: 12, md: 8}}>{metadataUrlField}</Grid.Col>
            </Grid>
            {showClaimFields && claimFields}
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 6}}>{domainsField}</Grid.Col>
            </Grid>
            <Group gap="sm">
              <Button type="submit" loading={isCreating} disabled={isBusy}>
                Enable SSO
              </Button>
              {cancelButton}
            </Group>
          </Stack>
        </form>
      );
    }

    const editableFields = provider.managedInApp ? (
      <>
        <Grid.Col span={{base: 12, md: 4}}>{presetField}</Grid.Col>
        <Grid.Col span={{base: 12, md: 8}}>{metadataUrlField}</Grid.Col>
      </>
    ) : (
      <>
        <Grid.Col span={{base: 12, md: 4}}>
          <ViewField
            label="Identity Provider Type"
            value={resolvePresetLabel(provider.attributeMappingPreset)}
          />
        </Grid.Col>
        <Grid.Col span={{base: 12, md: 8}}>
          <ViewField label="Metadata URL" value={provider.metadataUrl} />
        </Grid.Col>
        {showClaimViewFields && claimViewRows}
      </>
    );

    const ownershipButton = provider.managedInApp ? (
      <Button
        variant="subtle"
        color="red"
        ml="auto"
        onClick={() => setIsRemoveOpen(true)}
        loading={isDeleting}
        disabled={isBusy}>
        Remove SSO
      </Button>
    ) : (
      <Button
        variant="subtle"
        color="blue"
        ml="auto"
        onClick={() => setIsAdoptOpen(true)}
        loading={isAdopting}
        disabled={isBusy}>
        Adopt into app
      </Button>
    );

    return (
      <form onSubmit={handleSubmit(save)}>
        <Stack gap="md">
          <Grid gutter="md">
            <Grid.Col span={12}>
              <ViewField label="Provider Name" value={provider.providerName} />
            </Grid.Col>
            {editableFields}
          </Grid>
          {provider.managedInApp && showClaimFields && claimFields}
          <Grid gutter="md">
            <Grid.Col span={{base: 12, md: 6}}>{domainsField}</Grid.Col>
          </Grid>
          {serviceProviderBlock}
          {!provider.managedInApp && (
            <Text size="xs" c="dimmed">
              &quot;Adopt into app&quot; gives this instance its own app client
              so the app manages the provider from here on.
            </Text>
          )}
          <Divider my="md" />
          <Group gap="sm">
            <Button type="submit" loading={isUpdating} disabled={isBusy}>
              Save Single Sign-On
            </Button>
            {cancelButton}
            {ownershipButton}
          </Group>
        </Stack>
      </form>
    );
  };

  return (
    <SectionCard
      title="Single Sign-On (SAML)"
      rightSection={statusPill}
      data-testid="section-sso-provider">
      <Box p="md">{renderBody()}</Box>

      <ConfirmDialog
        opened={isRemoveOpen}
        onClose={() => setIsRemoveOpen(false)}
        onConfirm={handleRemove}
        title="Remove single sign-on?"
        body={`Removing single sign-on deletes ${providerLabel} identity provider and its Cognito app client. Users of this provider lose access immediately; their Proact accounts are not deleted. This cannot be undone.`}
        confirmationButtonColor="red"
        buttonTextYes="Remove SSO"
      />

      <ConfirmDialog
        opened={isAdoptOpen}
        onClose={() => setIsAdoptOpen(false)}
        onConfirm={handleAdopt}
        title={`Adopt ${adoptTarget} into the app?`}
        body={`Proact creates its own Cognito app client for ${adoptTarget} and moves sign-ins to it immediately. Existing users and the provider name are unchanged. This cannot be undone from this screen.`}
        buttonTextYes="Adopt into app"
      />
    </SectionCard>
  );
};
